import React, { FC, memo } from 'react';

import { Box, Typography, useTheme, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import PlayLargeIcon from '~/assets/icons/PlayLargeIcon';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import CardImageLabel from '~/components/Card/components/CardImageLabel/CardImageLabel';
import ToggleFromList from '~/components/ToggleFromList/ToggleFromList';
import ZoomedBackground, { createCustomDimensionsImgWrapper } from '~/components/ZoomedBackground';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { getCategoriesArray } from '~/pages/DiscoverPage/components/Hit/Hit';
import { HITS_TYPES } from '~/pages/const';
import { buildInsightsRoute, DiscoverRoutes } from '~/routes';
import { css } from '~/styled';
import { getDefaultTransition, SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX } from '~/theme';
import { formatDateToNewsFormat } from '~/utils/dateUtils/formatDateToNewsFormat';
import { secondsToHHHmmm } from '~/utils/dateUtils/secondsToHHHmmm';

interface NewsListItemProps {
  data?: any;
  type: 'post' | 'video';
  loading: boolean;
}

const getTransformedObjectData = (data, type, lang, tenantId) => {
  const categories = getCategoriesArray({
    item: {
      difficulty: data.difficulty,
      categories_array: data.categories,
      searchable_type: HITS_TYPES.VIDEO,
    },
    isGridHit: true,
    locale: lang,
    availableIDs: tenantId,
  });
  const accompanyingText =
    type === 'post'
      ? formatDateToNewsFormat({ dateString: data?.published_at, locale: lang }) +
        ' • ' +
        data?.user?.name
      : data?.user?.name + ' • ' + secondsToHHHmmm(data?.duration);
  const imageUrl = type === 'post' ? data?.image_url : data?.preview_url;
  return { categories, accompanyingText, imageUrl };
};

const NewsListItem: FC<NewsListItemProps> = memo(({ data, type, loading }) => {
  const theme = useTheme();
  const { t, i18n } = useTenantTranslation();
  const { hasAccessToContent } = useUserInfoContext();
  const { tenant } = useTenantContext();

  const isInWatchlist = data?.isInWatchList;

  if (loading) {
    return (
      <Wrapper>
        <LoadingCard />
      </Wrapper>
    );
  }

  const { categories, accompanyingText, imageUrl } = getTransformedObjectData(
    data,
    type,
    i18n.language,
    tenant?.id,
  );

  if (type === 'post') {
    return (
      <Wrapper
        data-testid="postListItem"
        path={imageUrl}
        imgWrapperCss={createCustomDimensionsImgWrapper({ height: '60%' })}
      >
        <StyledLink to={buildInsightsRoute(data.slug || data.id)}>
          <StyledCardImageLabel
            name={t('page.discover.insight')}
            bgColor={theme.palette.common.blueLight}
            textColor={theme.palette.common.white}
          />
        </StyledLink>
        <InfoWrap>
          <StyledTitleLink to={data?.link || ''}>
            <Title variant="h2" data-testid="postListItemTitle">
              {data?.title}
            </Title>
          </StyledTitleLink>
          <Description>{accompanyingText}</Description>
        </InfoWrap>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      isDark
      data-testid="videoListItem"
      path={imageUrl}
      imgWrapperCss={createCustomDimensionsImgWrapper({ height: '60%' })}
    >
      <StyledLink to={`${DiscoverRoutes.Videos}/${data.video_url || data.id}`} isDark>
        <PlayIconWrap>
          <PlayIcon color={theme.palette.common.white} />
        </PlayIconWrap>
      </StyledLink>
      <InfoWrap>
        <LabelsWrapper>
          {categories && categories.length > 0 && (
            <CategoriesWrapper>{categories}</CategoriesWrapper>
          )}
        </LabelsWrapper>
        <StyledTitleLink to={data?.link || ''}>
          <Title variant="h2">{data?.title}</Title>
        </StyledTitleLink>
        <DescriptionContainer>
          <Description>{accompanyingText}</Description>
          {hasAccessToContent && (
            <ToggleFromList
              id={data.id}
              noBackground
              placement="bottom"
              existInWatchlist={isInWatchlist}
            />
          )}
        </DescriptionContainer>
      </InfoWrap>
    </Wrapper>
  );
});

export default NewsListItem;

const StyledTitleLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'comingSoon' })<{
  comingSoon?: boolean;
}>`
  pointer-events: ${({ comingSoon }) => (comingSoon ? 'none' : 'auto')};
`;
const StyledCardImageLabel = styled(CardImageLabel)`
  position: absolute;
  top: 12px;
  left: 12px;
`;
const Wrapper = styled(ZoomedBackground, {
  shouldForwardProp: (prop) => prop !== 'isDark',
})<{ isDark?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 320px;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: ${SMALL_MARGIN_PX};
  background: ${({ theme, isDark }) =>
    isDark
      ? theme.palette.common.blockBackground.light
      : theme.palette.common.blockBackground.main};
  position: relative;
  transition: ${getDefaultTransition('background-color')};

  &:hover {
    & > a {
      &:before {
        opacity: 1;
      }
    }
    background: ${({ theme }) => theme.palette.common.card.hover};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: 337px;
  }
`;
const backgroundShadowCss = css`
  content: '';
  display: block;
  position: absolute;
  left: 0;
  bottom: 16px;
  width: 100%;
  height: 90%;
  -webkit-background-origin: padding-box;
  -o-background-origin: padding-box;
  -moz-background-origin: padding-box;
`;
const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'comingSoon',
})<{ isDark?: boolean; comingSoon?: boolean }>`
  width: 100%;
  height: 208px;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: ${({ comingSoon }) => (comingSoon ? 'none' : 'auto')};

  &::after {
    ${backgroundShadowCss};
    background: linear-gradient(
        0deg,
        ${({ theme, isDark }) =>
            isDark
              ? theme.palette.common.blockBackground.light
              : theme.palette.common.blockBackground.main}
          0%,
        ${({ theme, isDark }) =>
            alpha(
              isDark
                ? theme.palette.common.blockBackground.light
                : theme.palette.common.blockBackground.main,
              0.9,
            )}
          17%,
        ${({ theme, isDark }) =>
            alpha(
              isDark
                ? theme.palette.common.blockBackground.light
                : theme.palette.common.blockBackground.main,
              0,
            )}
          100%
      )
      0% 0% no-repeat;
  }
  &::before {
    ${backgroundShadowCss};
    height: 40%;
    transition: ${getDefaultTransition('opacity')};
    background: ${({ theme }) => theme.palette.common.card.hoverGradient};
    opacity: 0;
    z-index: 1;
  }
`;
const PlayIconWrap = styled(Box)`
  position: absolute;
  width: 64px;
  height: 64px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  border-radius: 50%;
  background: ${({ theme }) => alpha(theme.palette.common.blueDark, 0.5)};
`;
const PlayIcon = styled(PlayLargeIcon)`
  width: 28px;
  height: 32px;
  margin-left: 6px;
`;
const InfoWrap = styled(Box)`
  position: relative;
  padding: 0 ${SUBMIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
`;
const LabelsWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  & .cpdLabel {
    padding: 4.5px 9px;
  }
`;
const CategoriesWrapper = styled(Box)`
  display: inline-flex;
  flex-wrap: wrap;
  z-index: 1;

  & > a:not(:last-child) {
    margin-right: 9px;
    margin-bottom: 9px;
  }

  & > div {
    margin-right: 9px;
    margin-bottom: 9px;

    &:last-child {
      margin-right: 0;
    }
  }

  &:not(:only-child) {
    margin-right: 10px;
  }
`;
const Title = styled(Typography)`
  && {
    font-size: 16px;
    line-height: 1.2;
    font-weight: bold;
    margin-bottom: 9px;
    color: ${({ theme }) => theme.palette.common.white};
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
    }
  }
`;
const DescriptionContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Description = styled(Typography)`
  font-size: 14px;
  line-height: 1.2;
  font-weight: normal;
  color: ${({ theme }) => theme.palette.common.text.meta};
`;
const LoadingCard = styled(AnimatedSkeleton)`
  height: 320px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 100%;
  }
`;
